import type { AccuracyConfig } from "@/types/participant";
import type { Participant } from "@inloco/shared/types/participant.types";
import type { TFunction } from "i18next";

export const ICONS = {
    group: 'communities',
    pois: 'map',
    session: 'location_on',
    qrCodeScanner: 'qr_code_scanner',
    shareWithPeople: 'connect_without_contact'
}

export const ICONS_STYLE = {
    weight: '200',
    family: 'materials'
}

export const getParticipantPopupContent = (participant: Participant, radius: number | null, t: TFunction) => {
    const participantBadge = participant.type === 'device' ? t('map.tel') : t('map.ble');
    const participantLabel = participant.type === 'device' ? t('map.phone') : t('map.bracelettag');
    const metricLabel = t('map.accuracy');
    const metricValue = radius !== null ? `~${Math.round(radius)} m` : '--';

    return `
            <div style="min-width: 220px; max-width: 260px; font-family: Inter, system-ui, -apple-system, sans-serif; color: #0f172a; line-height: 1.4; padding: 2px;">
                <div style="font-size: 14px; font-weight: 700; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                    <span>${participant.name}</span>
                    <span style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; background: #f8fafc; border: 1px solid #e2e8f0; padding: 3px 8px; border-radius: 999px;">
                        ${participantBadge}
                    </span>
                </div>

                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px;">
                    <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 600; margin-bottom: 6px;">
                        ${t('map.locationStatus')}
                    </div>

                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; gap: 12px;">
                        <span style="font-size: 11px; color: #334155;">${t('map.type')}:</span>
                        <strong style="font-size: 11px; color: #0f172a;">${participantLabel}</strong>
                    </div>

                    <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                        <span style="font-size: 11px; color: #334155;">${metricLabel}:</span>
                        <strong style="font-size: 11px; color: #0f172a;">${metricValue}</strong>
                    </div>
                </div>
            </div>
        `;
};

const getAccuracyConfig = (t: TFunction, rawAccuracy: number | null): AccuracyConfig => {
    if (rawAccuracy === null) {
        return {
            level: t('map.signalNotAvailable'),
            color: '#64748b',
            hint: t('map.signalCalculationInProgress'),
        };
    }

    if (rawAccuracy <= 10) {
        return {
            level: t('map.signalHigh'),
            color: '#16a34a',
            hint: t('map.signalVeryPrecise'),
        };
    }

    if (rawAccuracy <= 30) {
        return {
            level: t('map.signalGood'),
            color: '#22c55e',
            hint: t('map.signalReliable'),
        };
    }

    if (rawAccuracy <= 70) {
        return {
            level: t('map.signalMedium'),
            color: '#ea580c',
            hint: t('map.signalIndicative'),
        };
    }

    return {
        level: t('map.signalLow'),
        color: '#dc2626',
        hint: t('map.signalWeak'),
    };
}

export const buildUserMarkerPopupContent = (
    t: TFunction,
    rawAccuracy: number | null,
    effectiveAccuracy: number
) => {
    const accuracyConfig = getAccuracyConfig(t, rawAccuracy);

    return `
            <div style="min-width: 240px; max-width: 270px; font-family: Inter, system-ui, -apple-system, sans-serif; color: #0f172a; line-height: 1.4; padding: 2px;">
                <div style="font-size: 14px; font-weight: 700; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between;">
                    <span>${t('map.youAreHere')}</span>
                </div>

                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; margin-bottom: 10px;">
                    <div style="font-size: 11px; text-transform: uppercase; tracking: 0.05em; color: #64748b; font-weight: 600; margin-bottom: 6px;">
                        ${t('map.gpsSignalStatus')}
                    </div>
                    
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                        <span style="font-size: 11px; color: #334155;">${t('map.accuracy')}:</span>
                        <strong style="font-size: 11px; color: #0f172a;">
                            ${rawAccuracy !== null ? `~${Math.round(rawAccuracy)} m` : '--'}
                        </strong>
                    </div>

                    <div style="display: flex; align-items: center; justify-content: space-between;">
                        <span style="font-size: 11px; color: #334155;">${t('map.signalQuality')}:</span>
                        <div style="display: flex; align-items: center; gap: 5px; background: ${accuracyConfig.color}15; padding: 2px 8px; border-radius: 4px;">
                            <span style="width: 6px; height: 6px; background: ${accuracyConfig.color}; border-radius: 50%;"></span>
                            <strong style="font-size: 11px; color: ${accuracyConfig.color}; font-weight: 700;">
                                ${accuracyConfig.level}
                            </strong>
                        </div>
                    </div>
                </div>

                <div style="font-size: 11px; color: #475569; background: #fff; padding: 0 2px;">
                    ${accuracyConfig.hint}
                    ${rawAccuracy && rawAccuracy > 10 ? (`
                        <span style="color: #94a3b8;">
                            ${t('map.signalCircleHint', { radius: Math.round(effectiveAccuracy) })}
                        </span>
                    `) : ''}
                </div>
                
            </div>
        `;
}